import React from "react";
import { motion } from "framer-motion";
import { Crown, Medal, Award, Share2, Download, Check } from "lucide-react";
import { toast } from "sonner";
import { resolveAsset } from "../lib/api";

function initials(n) {
  return (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
}

const PODIUM = [
  { place: "1st", icon: Crown, height: "h-48", color: "#facc15", glow: "rgba(253,224,71,0.6)", label: "CHAMPION" },
  { place: "2nd", icon: Medal, height: "h-40", color: "#e5e7eb", glow: "rgba(229,231,235,0.45)", label: "RUNNER-UP" },
  { place: "3rd", icon: Medal, height: "h-32", color: "#f97316", glow: "rgba(249,115,22,0.45)", label: "BRONZE" },
  { place: "4th", icon: Award, height: "h-24", color: "#a1a1aa", glow: "rgba(161,161,170,0.35)", label: "4TH PLACE" },
];

/** Podium shown when a Group Advancement tournament finishes. */
export default function GroupPodium({ placements, teamsById, tournamentId }) {
  const [copied, setCopied] = React.useState(false);
  if (!placements || placements.length < 2) return null;
  const rows = PODIUM.map((slot, i) => ({
    ...slot,
    team: placements[i] ? teamsById[placements[i]] : null,
  })).filter((r) => r.team);
  if (rows.length < 2) return null;

  // Podium ordering on screen: 2nd, 1st, 3rd, 4th (centered champion)
  const ordered = [];
  if (rows[1]) ordered.push(rows[1]);
  if (rows[0]) ordered.push(rows[0]);
  if (rows[2]) ordered.push(rows[2]);
  if (rows[3]) ordered.push(rows[3]);

  const backendBase = process.env.REACT_APP_BACKEND_URL || "";
  const podiumImgUrl = tournamentId ? `${backendBase}/api/og/podium/${tournamentId}.png` : null;
  const shareUrl = tournamentId
    ? `${window.location.origin}/api/share/podium/${tournamentId}`
    : null;

  const copyShare = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Share link copied — paste in Discord or X");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  const downloadImg = () => {
    if (!podiumImgUrl) return;
    const a = document.createElement("a");
    a.href = podiumImgUrl;
    a.download = `spicy-podium-${tournamentId}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div
      data-testid="group-podium"
      className="glass-dark rounded-2xl p-6 mt-6 relative overflow-hidden"
    >
      <div className="absolute inset-0 -z-0 opacity-40 pointer-events-none bg-[radial-gradient(ellipse_60%_40%_at_50%_10%,rgba(253,224,71,0.25),transparent_70%)]" />
      <div className="relative flex items-center justify-between mb-8">
        <h3 className="font-display font-bold text-xl flex items-center gap-2">
          <Crown size={18} className="text-yellow-300" fill="currentColor" /> Final Placements
        </h3>
        <div className="flex items-center gap-2">
          {tournamentId && (
            <>
              <button
                onClick={copyShare}
                data-testid="podium-share-btn"
                className="btn-ghost-glass !py-1.5 !px-3 !text-[11px] flex items-center gap-1.5"
                title="Copy shareable link"
              >
                {copied ? <Check size={12} /> : <Share2 size={12} />}
                {copied ? "Copied" : "Share"}
              </button>
              <button
                onClick={downloadImg}
                data-testid="podium-download-btn"
                className="btn-ghost-glass !py-1.5 !px-3 !text-[11px] flex items-center gap-1.5"
                title="Download podium image"
              >
                <Download size={12} /> PNG
              </button>
            </>
          )}
          <span className="text-[10px] uppercase tracking-[0.25em] text-yellow-300/70 font-mono hidden sm:inline">
            Podium
          </span>
        </div>
      </div>
      <div className="relative flex items-end justify-center gap-3 sm:gap-5 flex-wrap">
        {ordered.map((r, idx) => {
          const Icon = r.icon;
          return (
            <motion.div
              key={r.place}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: idx * 0.12, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`podium-${r.place}`}
              className="flex flex-col items-center w-24 sm:w-32"
            >
              <Icon
                size={r.place === "1st" ? 28 : 20}
                className="mb-2"
                fill={r.place === "1st" ? r.color : "transparent"}
                color={r.color}
                style={{ filter: `drop-shadow(0 0 10px ${r.glow})` }}
              />
              {r.team.logo_url ? (
                <img
                  src={resolveAsset(r.team.logo_url)}
                  alt=""
                  className="h-12 w-12 rounded-full object-cover border-2"
                  style={{ borderColor: r.color, boxShadow: `0 0 18px ${r.glow}` }}
                />
              ) : (
                <div
                  className="h-12 w-12 rounded-full flex items-center justify-center font-display font-bold text-white border-2"
                  style={{
                    background: r.team.color || "#2a2a2a",
                    borderColor: r.color,
                    boxShadow: `0 0 18px ${r.glow}`,
                  }}
                >
                  {initials(r.team.name)}
                </div>
              )}
              <div className="mt-2 font-semibold text-sm text-center truncate w-full" title={r.team.name}>
                {r.team.name}
              </div>
              <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-zinc-400 mt-0.5">
                {r.label}
              </div>
              <div
                className={`relative mt-3 ${r.height} w-full rounded-t-md border-t-2 flex items-start justify-center pt-2`}
                style={{
                  background: `linear-gradient(180deg, ${r.color}33 0%, ${r.color}11 50%, transparent 100%)`,
                  borderColor: r.color,
                }}
              >
                <span
                  className="font-display font-black text-3xl"
                  style={{ color: r.color, textShadow: `0 0 12px ${r.glow}` }}
                >
                  {r.place}
                </span>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
